import { useState } from 'react';

import { ApiError, api } from '@/api/client';
import { useApi } from '@/api/useApi';
import type { Team } from '@/api/types';
import {
  Button,
  Card,
  CardHead,
  ErrorState,
  Field,
  Loading,
  Modal,
  Notice,
  Page,
  PageHead,
  Tag,
  Segmented,
  Table,
  TextInput,
} from '@/components/ui';
import { formatDate, humanise } from '@/lib/format';

/**
 * Team and access.
 *
 * Family invitations are deliberately not issued from here — they need a care
 * recipient and a recorded consent reference, so they live on the client
 * record where both are in front of you.
 */
export function TeamPage() {
  const { data, error, loading, reload } = useApi<Team>('/admin/team');
  const [inviting, setInviting] = useState(false);

  if (loading) return <Loading />;
  if (error) return <Page><ErrorState message={error.message} onRetry={reload} /></Page>;

  const users = data?.users ?? [];
  const invitations = data?.invitations ?? [];
  const adminsWithoutMfa = users.filter(
    (user) => user.role === 'admin' && user.status === 'active' && !user.mfa_enrolled,
  );

  return (
    <Page>
      <PageHead
        title="Team"
        subtitle="Accounts exist only by invitation"
        actions={<Button onClick={() => setInviting(true)}>Invite a colleague</Button>}
      />

      <div className="stack">
        {adminsWithoutMfa.length > 0 ? (
          <Notice tone="attention" title="Administrators without multi-factor authentication">
            {adminsWithoutMfa.map((user) => user.full_name).join(', ')} — they will be required to
            enrol at their next sign-in.
          </Notice>
        ) : null}

        <Table columns={['Person', 'Role', 'Status', 'MFA', 'Last signed in']}>
          {users.map((user) => (
            <tr key={user.id}>
              <td>
                <div className="strong">{user.full_name}</div>
                <div className="meta">{user.email ?? 'No email'}</div>
              </td>
              <td>{humanise(user.role)}</td>
              <td>
                <Tag
                  label={humanise(user.status)}
                  tone={user.status === 'active' ? 'positive' : 'attention'}
                />
              </td>
              <td>
                {user.role !== 'admin' ? (
                  <span className="meta">Not required</span>
                ) : user.mfa_enrolled ? (
                  <Tag label="Enrolled" tone="positive" />
                ) : (
                  <Tag label="Not enrolled" tone="urgent" />
                )}
              </td>
              <td className="meta">
                {user.last_login_at ? formatDate(user.last_login_at) : 'Never'}
              </td>
            </tr>
          ))}
        </Table>

        <Card variant="flush">
          <CardHead title="Invitations" />
          <Table columns={['Name', 'Email', 'Role', 'State', 'Expires', '']} empty="None sent.">
            {invitations.map((invitation) => {
              const state = invitation.accepted_at
                ? { label: 'Accepted', tone: 'positive' as const }
                : invitation.revoked_at
                  ? { label: 'Revoked', tone: 'neutral' as const }
                  : invitation.is_usable
                    ? { label: 'Pending', tone: 'info' as const }
                    : { label: 'Expired', tone: 'neutral' as const };

              return (
                <tr key={invitation.id}>
                  <td className="strong">{invitation.full_name}</td>
                  <td>{invitation.email}</td>
                  <td>{humanise(invitation.role)}</td>
                  <td>
                    <Tag label={state.label} tone={state.tone} />
                  </td>
                  <td className="meta">{formatDate(invitation.expires_at)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {invitation.is_usable && !invitation.accepted_at ? (
                      <Button
                        variant="urgent"
                        onClick={async () => {
                          await api.del(`/admin/invitations/${invitation.id}`);
                          reload();
                        }}
                      >
                        Revoke
                      </Button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </Table>
        </Card>

        <Card>
          <p className="meta">
            To give a family member access, open the client record — a family invitation needs the
            care recipient and a recorded consent reference, and access is not granted without
            both.
          </p>
        </Card>
      </div>

      {inviting ? (
        <InviteColleagueModal
          onClose={() => setInviting(false)}
          onInvited={() => {
            setInviting(false);
            reload();
          }}
        />
      ) : null}
    </Page>
  );
}

function InviteColleagueModal({
  onClose,
  onInvited,
}: {
  onClose: () => void;
  onInvited: () => void;
}) {
  const [role, setRole] = useState<'caregiver' | 'admin'>('caregiver');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setProblem(null);
    try {
      const response = await api.post<{ invitation_token: string }>('/admin/invitations', {
        email: email.trim(),
        full_name: fullName.trim(),
        role,
      });
      setToken(response.invitation_token);
    } catch (caught) {
      setProblem((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  if (token) {
    return (
      <Modal title="Invitation created" onClose={onInvited}>
        <div className="stack">
          <Notice tone="positive" title="Send this link out of band">
            It is shown once and never again.
          </Notice>
          <Card>
            <code className="numeric" style={{ wordBreak: 'break-all' }}>
              familycare://invite?token={token}
            </code>
          </Card>
          <Button onClick={onInvited} block>
            Done
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title="Invite a colleague" onClose={onClose}>
      <div className="stack">
        {problem ? (
          <Notice tone="urgent" title="Could not create the invitation">
            {problem}
          </Notice>
        ) : null}

        <div>
          <span className="field__label" style={{ display: 'block', marginBottom: 4 }}>
            Role
          </span>
          <Segmented
            label="Role"
            options={[
              { value: 'caregiver' as const, label: 'Caregiver' },
              { value: 'admin' as const, label: 'Administrator' },
            ]}
            value={role}
            onChange={setRole}
          />
        </div>

        {role === 'admin' ? (
          <Notice tone="info" title="Administrators must enrol in MFA">
            They will be asked to set up an authenticator app at first sign-in, and cannot sign in
            without it.
          </Notice>
        ) : null}

        <Field label="Full name">
          {(props) => (
            <TextInput {...props} value={fullName} onChange={(e) => setFullName(e.target.value)} autoFocus />
          )}
        </Field>
        <Field label="Email">
          {(props) => (
            <TextInput {...props} type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          )}
        </Field>

        <div className="row row--between">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} busy={busy} disabled={!fullName.trim() || !email.trim()}>
            Create invitation
          </Button>
        </div>
      </div>
    </Modal>
  );
}
